import * as React from 'react'
import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Input from '@mui/joy/Input'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Typography from '@mui/joy/Typography'
import Autocomplete from '@mui/joy/Autocomplete'
import { Button, FormControl, FormLabel, Sheet } from '@mui/joy'
import { Pagination } from '@mui/material'
import EmailList from './EmailList'
import EmailSidebar from './EmailSidebar'
import { EmailRecord } from './types'
import axios from 'axios'

interface AllEmailsTabProps {
    projectName: string
}
export default function AllEmailsTab({ projectName }: AllEmailsTabProps) {
    const [emails, setEmails] = React.useState<EmailRecord[]>([])
    const [keywords, setKeywords] = React.useState<string[]>([])
    const [startDate, setStartDate] = React.useState('')
    const [endDate, setEndDate] = React.useState('')
    const [threadCount, setThreadCount] = React.useState<string>('3')
    const [resultCount, setResultCount] = React.useState<number>(0)
    const [fetchTime, setFetchTime] = React.useState<number>(0)
    const [loading, setLoading] = React.useState(false)
    const [selectedEmail, setSelectedEmail] = React.useState<EmailRecord | null>(null)
    const [sidebarOpen, setSidebarOpen] = React.useState(false)
    const [thread, setThread] = React.useState<any[]>([])
    const [threadCountState, setThreadCountState] = React.useState<number>(0)
    const [threadFetchTime, setThreadFetchTime] = React.useState<number>(0)
    const [page, setPage] = React.useState(1)
    const [pageSize, setPageSize] = React.useState(50)
    const pageCount = React.useMemo(() => Math.ceil(resultCount / pageSize), [resultCount, pageSize])
    const firstLoadedRef = React.useRef(false)

    // Fetch emails from backend
    const fetchEmails = React.useCallback(async () => {
        if (!projectName) return
        setLoading(true)
        const start = performance.now()
        try {
            const params = new URLSearchParams()
            if (keywords.length > 0) keywords.forEach((k) => params.append('keywords', k))
            if (startDate) params.append('startDate', String(new Date(startDate).getTime()))
            if (endDate) params.append('endDate', String(new Date(endDate).getTime()))
            if (threadCount) params.append('threadsCount', threadCount)
            params.append('page', String(page))
            params.append('pageSize', String(pageSize))

            const { data } = await axios.get(`/gluon/applications/${projectName}/emails`, { params })
            setEmails(data.emails ?? [])
            setResultCount(data.total ?? 0)
            setFetchTime(Math.round(performance.now() - start))
        } catch (err) {
            setEmails([])
            setResultCount(0)
            setFetchTime(Math.round(performance.now() - start))
        } finally {
            setLoading(false)
        }
    }, [projectName, keywords, startDate, endDate, threadCount, page, pageSize])

    // Set default date range: 1 month ago to today
    React.useEffect(() => {
        if (!startDate && !endDate) {
            const today = new Date()
            const prior = new Date()
            prior.setMonth(today.getMonth() - 1)
            setStartDate(prior.toISOString().slice(0, 10))
            setEndDate(today.toISOString().slice(0, 10))
        }
    }, [startDate, endDate, page])

    // Trigger fetch on search button click
    const handleSearch = () => {
        setPage(1)
        fetchEmails()
    }

    React.useEffect(() => {
        if (firstLoadedRef.current) {
            fetchEmails()
        } else {
            firstLoadedRef.current = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    return (
        <Box>
            <Sheet variant="outlined" sx={{ mb: 2, px: 2, py: 1, borderRadius: 8, display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography level="body-sm">
                    Results: <b>{resultCount}</b>
                </Typography>
                <Typography level="body-sm">
                    Fetch time: <b>{fetchTime}</b> ms
                </Typography>
            </Sheet>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, alignItems: { sm: 'center' }, flexWrap: 'wrap' }}>
                <FormControl size="sm" sx={{ minWidth: 220 }}>
                    <FormLabel>Keywords</FormLabel>
                    <Autocomplete multiple freeSolo value={keywords} onChange={(_, value) => setKeywords(value)} options={[]} placeholder="Enter keywords..." />
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>Start date</FormLabel>
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} slotProps={{ input: { 'aria-label': 'Start date' } }} />
                </FormControl>
                <Typography level="body-sm" sx={{ alignSelf: { xs: 'center', sm: 'end' } }}>
                    to
                </Typography>
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>End date</FormLabel>
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} slotProps={{ input: { 'aria-label': 'End date' } }} />
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 140 }}>
                    <FormLabel>Threads count</FormLabel>
                    <Select placeholder="Threads count" value={threadCount} onChange={(_, v) => setThreadCount(v ?? '')}>
                        {[...Array(5)].map((_, i) => {
                            const val = String(i + 1)
                            return (
                                <Option key={val} value={val}>
                                    {val}+
                                </Option>
                            )
                        })}
                    </Select>
                </FormControl>
                <Button variant="soft" color="primary" sx={{ minWidth: 140 }} loading={loading} onClick={handleSearch}>
                    Search
                </Button>
            </Stack>
            {/* Email List and Sidebar */}
            <Box sx={{ display: 'flex', flexDirection: 'row', mt: 4, position: 'relative' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EmailList
                        emails={emails}
                        onSelect={async (email) => {
                            setSelectedEmail(email)
                            setSidebarOpen(true)
                            if (projectName && email?.emailData?.object?.thread_id) {
                                try {
                                    const start = performance.now()
                                    const { data } = await axios.get(`/gluon/applications/${projectName}/emails/thread/${email.emailData.object.thread_id}`)
                                    setThread(data.thread || [])
                                    setThreadCountState(data.count || 0)
                                    setThreadFetchTime(data.fetchTime || Math.round(performance.now() - start))
                                } catch {
                                    setThread([])
                                    setThreadCountState(0)
                                    setThreadFetchTime(0)
                                }
                            } else {
                                setThread([])
                                setThreadCountState(0)
                                setThreadFetchTime(0)
                            }
                        }}
                        selectedId={selectedEmail?.emailData.object.id}
                    />
                    {/* Pagination controls */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                        <Box>
                            <FormControl orientation="horizontal" size="sm">
                                <FormLabel>Rows per page</FormLabel>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value))
                                        setPage(1)
                                    }}
                                    style={{ fontSize: 14, padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc' }}
                                >
                                    {[10, 25, 50].map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                            </FormControl>
                        </Box>
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            siblingCount={1}
                            boundaryCount={1}
                            size="small"
                            sx={{
                                '& .MuiPagination-ul': {
                                    flexWrap: 'nowrap',
                                },
                                '& .MuiPaginationItem-root': {
                                    minWidth: 32,
                                    height: 32,
                                },
                            }}
                        />
                    </Box>
                </Box>
                <EmailSidebar
                    open={sidebarOpen}
                    email={selectedEmail}
                    thread={thread}
                    threadCount={threadCountState}
                    threadFetchTime={threadFetchTime}
                    onClose={() => setSidebarOpen(false)}
                />
            </Box>
        </Box>
    )
}

// --- No more mock data: all emails now fetched from backend ---
